import { motion } from "framer-motion";

const T = "#10B8B1";

const draw = (delay = 0, duration = 1.4) => ({
  initial: { pathLength: 0, opacity: 0 },
  whileInView: { pathLength: 1, opacity: 1 },
  viewport: { once: true, amount: 0.4 },
  transition: { duration, delay, ease: "easeOut" },
});

const fade = (delay = 0) => ({
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true },
  transition: { duration: 0.8, delay },
});

export const SignalHead = () => (
  <svg viewBox="0 0 300 210" fill="none" className="w-full max-w-[320px]" aria-hidden="true">
    <motion.rect x="10" y="14" width="132" height="182" rx="12" stroke={T} strokeOpacity="0.35" {...fade(0.1)} />
    <motion.text x="26" y="40" fill={T} fontSize="8.5" letterSpacing="2.5" fontFamily="'JetBrains Mono', monospace" {...fade(0.2)}>TRACKED</motion.text>
    <motion.text x="26" y="52" fill="#64748B" fontSize="7" letterSpacing="2" fontFamily="'JetBrains Mono', monospace" {...fade(0.3)}>PHYSICAL STATE</motion.text>

    <motion.path
      {...draw(0.4, 1.1)}
      d="M76 112 C 60 100 52 89 55 78 C 57.5 69.5 67 66.5 76 75 C 85 66.5 94.5 69.5 97 78 C 100 89 92 100 76 112 Z"
      stroke="#F8FAFC" strokeOpacity="0.6" strokeWidth="1.5" strokeLinejoin="round"
    />
    <motion.path
      {...draw(0.7, 1)}
      d="M24 130 H50 L56 118 L64 140 L70 124 L76 130 H106"
      stroke={T} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
    />
    <motion.text x="26" y="162" fill="#F8FAFC" fontSize="12" fontFamily="'JetBrains Mono', monospace" {...fade(1.0)}>
      72.4 <tspan fontSize="7" fill="#64748B" letterSpacing="1.5">BPM</tspan>
    </motion.text>
    <motion.text x="102" y="161" fill={T} fontSize="7" fontFamily="'JetBrains Mono', monospace" {...fade(1.1)}>±0.1</motion.text>
    <motion.path {...draw(1.1, 0.8)} d="M26 174 H126" stroke={T} strokeWidth="2.5" strokeLinecap="round" />
    <motion.text x="26" y="190" fill="#64748B" fontSize="7" letterSpacing="1.5" fontFamily="'JetBrains Mono', monospace" {...fade(1.3)}>measured directly</motion.text>

    <motion.line x1="150" y1="26" x2="150" y2="184" stroke={T} strokeOpacity="0.15" strokeDasharray="2 6" {...fade(0.5)} />

    <motion.rect x="158" y="14" width="132" height="182" rx="12" stroke="#64748B" strokeOpacity="0.35" strokeDasharray="4 5" {...fade(0.1)} />
    <motion.text x="174" y="40" fill="#94A3B8" fontSize="8.5" letterSpacing="2.5" fontFamily="'JetBrains Mono', monospace" {...fade(0.2)}>INFERRED</motion.text>
    <motion.text x="174" y="52" fill="#64748B" fontSize="7" letterSpacing="2" fontFamily="'JetBrains Mono', monospace" {...fade(0.3)}>COGNITIVE STATE</motion.text>

    <motion.circle cx="224" cy="92" r="17" stroke="#94A3B8" strokeOpacity="0.7" strokeWidth="1.5" {...draw(0.4, 1)} />
    <motion.path {...draw(0.6, 0.8)} d="M224 76 V108" stroke="#94A3B8" strokeOpacity="0.4" strokeDasharray="2 3" />
    <motion.path {...draw(0.7, 0.8)} d="M214 87 C 217 83 221 85 221 90 M234 87 C 231 83 227 85 227 90" stroke="#94A3B8" strokeOpacity="0.7" strokeWidth="1.3" strokeLinecap="round" />
    <motion.path
      {...draw(0.8, 1.2)}
      d="M172 130 C 185 120 196 140 210 128 C 224 118 236 138 250 126 L 262 122"
      stroke="#94A3B8" strokeOpacity="0.5" strokeWidth="1.4" strokeDasharray="3 5" strokeLinecap="round"
    />
    <motion.text x="174" y="162" fill="#94A3B8" fontSize="12" fontFamily="'JetBrains Mono', monospace" {...fade(1.0)}>
      ≈ 62? <tspan fontSize="7" fill="#64748B" letterSpacing="1.5">FOCUS</tspan>
    </motion.text>
    <motion.text x="248" y="161" fill="#64748B" fontSize="7" fontFamily="'JetBrains Mono', monospace" {...fade(1.1)}>±30</motion.text>
    <motion.path {...fade(1.2)} d="M174 174 H246 M174 168 V174 M246 168 V174 M210 171 V174" stroke="#94A3B8" strokeOpacity="0.55" strokeDasharray="3 4" />
    <motion.text x="174" y="190" fill="#64748B" fontSize="7" letterSpacing="1.5" fontFamily="'JetBrains Mono', monospace" {...fade(1.3)}>guessed from proxies</motion.text>
  </svg>
);

export const AscentSignal = () => (
  <svg viewBox="0 0 280 200" fill="none" className="w-full max-w-[320px]" aria-hidden="true">
    {[130, 100, 70].map((y) => (
      <motion.line key={y} x1="22" y1={y} x2="258" y2={y} stroke={T} strokeOpacity="0.08" strokeDasharray="2 8" {...fade(0.3)} />
    ))}
    <motion.path {...draw(0.2, 1)} d="M22 162 H256 M248 157 L256 162 L248 167" stroke="#F8FAFC" strokeOpacity="0.3" strokeLinecap="round" />
    <motion.path {...draw(0.3, 0.9)} d="M22 162 V32" stroke="#F8FAFC" strokeOpacity="0.2" />

    {[[30, 150], [40, 142], [52, 154], [62, 138], [74, 150], [86, 132], [96, 144], [106, 128]].map(([cx, cy], i) => (
      <motion.circle key={i} cx={cx} cy={cy} r="1.4" fill={T} fillOpacity="0.3" {...fade(0.5 + i * 0.06)} />
    ))}

    <motion.path
      {...draw(0.9, 1.8)}
      d="M30 148 C 80 142 108 128 138 108 C 172 84 212 58 250 40"
      stroke={T} strokeWidth="1.8" strokeLinecap="round"
    />
    {[[80, 139], [138, 108], [196, 70]].map(([cx, cy], i) => (
      <motion.circle key={i} cx={cx} cy={cy} r="3" stroke={T} strokeWidth="1.4" {...fade(1.4 + i * 0.2)} />
    ))}
    <motion.circle cx="250" cy="40" r="5" fill={T} {...fade(2)} />
    <motion.circle cx="250" cy="40" r="11" stroke={T} strokeOpacity="0.35" {...fade(2.1)} />
    <motion.text x="30" y="26" fill="#64748B" fontSize="8" letterSpacing="2.5" fontFamily="'JetBrains Mono', monospace" {...fade(1.6)}>MEASUREMENT → INTELLIGENCE</motion.text>
  </svg>
);

export const ConvergeOrbits = () => (
  <svg viewBox="0 0 240 200" fill="none" className="w-full max-w-[280px]" aria-hidden="true">
    {[
      { cx: 94, cy: 84, d: 0 },
      { cx: 146, cy: 84, d: 0.25 },
      { cx: 120, cy: 120, d: 0.5 },
    ].map((c, i) => (
      <motion.circle
        key={i} cx={c.cx} cy={c.cy} r="54"
        stroke={T} strokeOpacity={0.5 - i * 0.08} strokeWidth="1.4"
        {...draw(c.d, 1.5)}
      />
    ))}
    {[[120, 96], [66, 70], [174, 70], [120, 172]].map(([cx, cy], i) => (
      <motion.circle key={i} cx={cx} cy={cy} r={i === 0 ? 4 : 2.4} fill={T} fillOpacity={i === 0 ? 1 : 0.6} {...fade(1.2 + i * 0.15)} />
    ))}
    <motion.circle cx="120" cy="96" r="11" stroke={T} strokeOpacity="0.3" {...fade(1.4)} />
    <motion.path {...fade(1.6)} d="M120 34 V44 M120 148 V158 M32 96 H42 M198 96 H208" stroke={T} strokeOpacity="0.3" strokeDasharray="2 4" />
  </svg>
);

export const ApertureScan = () => (
  <svg viewBox="0 0 300 200" fill="none" className="w-full max-w-[320px]" aria-hidden="true">
    <motion.text x="14" y="26" fill="#64748B" fontSize="7.5" letterSpacing="2.5" fontFamily="'JetBrains Mono', monospace" {...fade(0.2)}>RAW SIGNAL · NEUROSCIENCE</motion.text>
    <motion.text x="14" y="188" fill="#64748B" fontSize="7.5" letterSpacing="2.5" fontFamily="'JetBrains Mono', monospace" {...fade(0.2)}>ARTIFICIAL INTELLIGENCE</motion.text>
    <motion.text x="212" y="26" fill={T} fillOpacity="0.8" fontSize="7.5" letterSpacing="2.5" fontFamily="'JetBrains Mono', monospace" {...fade(1.4)}>PRECISION</motion.text>

    <motion.path
      {...draw(0.2, 1.4)}
      d="M16 52 L27 45 L34 63 L44 40 L52 68 L63 45 L72 61 L83 38 L93 66 L104 42 L114 58 L124 48 L136 84 L148 96"
      stroke={T} strokeOpacity="0.55" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"
    />

    {[[26, 128], [52, 120], [78, 132], [52, 148], [104, 122], [128, 134]].map(([cx, cy], i) => (
      <motion.circle key={i} cx={cx} cy={cy} r="2.4" stroke={T} strokeOpacity="0.8" strokeWidth="1.3" {...fade(0.5 + i * 0.1)} />
    ))}
    {[
      "M26 128 L52 120", "M52 120 L78 132", "M26 128 L52 148", "M52 148 L78 132",
      "M78 132 L104 122", "M104 122 L128 134", "M52 148 L104 122",
    ].map((d, i) => (
      <motion.path key={i} d={d} stroke={T} strokeOpacity="0.35" strokeWidth="1" {...draw(0.6 + i * 0.06, 0.5)} />
    ))}
    <motion.path {...draw(1.0, 0.8)} d="M128 134 C 138 126 142 112 150 101" stroke={T} strokeOpacity="0.7" strokeWidth="1.4" strokeLinecap="round" />

    <motion.line x1="152" y1="34" x2="152" y2="172" stroke={T} strokeOpacity="0.15" strokeDasharray="2 6" {...fade(1.1)} />

    <motion.circle cx="150" cy="100" r="16" fill={T} fillOpacity="0.08" {...fade(1.1)} />
    <motion.circle cx="150" cy="100" r="8" stroke={T} strokeOpacity="0.5" {...fade(1.15)} />
    <motion.circle
      cx="150" cy="100" r="3.5" fill={T}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: [0.6, 1, 0.6] }}
      viewport={{ once: true }}
      transition={{ delay: 1.2, duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
    />

    <motion.path
      {...draw(1.3, 1.6)}
      d="M156 100 C 168 100 170 74 184 74 C 198 74 200 100 214 100 C 228 100 230 74 244 74 C 258 74 260 100 274 100 L 290 100"
      stroke={T} strokeWidth="1.9" strokeLinecap="round"
    />
    <motion.path {...fade(1.7)} d="M156 140 H290" stroke={T} strokeOpacity="0.3" />
    {[166, 186, 206, 226, 246, 266, 286].map((x, i) => (
      <motion.line key={x} x1={x} y1="134" x2={x} y2="140" stroke={T} strokeOpacity="0.45" {...fade(1.8 + i * 0.05)} />
    ))}
    <motion.circle cx="184" cy="74" r="2.6" stroke={T} strokeWidth="1.3" {...fade(1.9)} />
    <motion.circle cx="244" cy="74" r="2.6" stroke={T} strokeWidth="1.3" {...fade(2.0)} />
  </svg>
);

export const PipelineFusion = () => (
  <svg viewBox="0 0 260 210" fill="none" className="w-full max-w-[300px]" aria-hidden="true">
    {[
      { y: 36, glyph: "M20 36 H24 L27 30 L31 42 L34 34 L36 36" },
      { y: 105, glyph: "M22 105 C22 101 26 101 27 104 C28 101 32 101 32 105 C32 108 27 111 27 111 C27 111 22 108 22 105 Z" },
      { y: 174, glyph: "M19 174 C22 170 32 170 35 174 C32 178 22 178 19 174 Z" },
    ].map((n, i) => (
      <g key={i}>
        <motion.circle cx="27" cy={n.y} r="11" stroke={T} strokeOpacity="0.7" {...draw(i * 0.15, 0.9)} />
        <motion.path d={n.glyph} stroke={T} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" {...draw(0.4 + i * 0.15, 0.8)} />
      </g>
    ))}
    {[
      "M38 36 C 100 36 120 105 188 105",
      "M38 105 H188",
      "M38 174 C 100 174 120 105 188 105",
    ].map((d, i) => (
      <motion.path
        key={i} d={d} stroke={T} strokeOpacity="0.55" strokeWidth="1.3" strokeDasharray="5 7"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5 + i * 0.1 }}
        animate={{ strokeDashoffset: [0, -48], transition: { repeat: Infinity, duration: 2.4, ease: "linear" } }}
      />
    ))}
    <motion.circle cx="205" cy="105" r="26" fill={T} fillOpacity="0.08" {...fade(0.9)} />
    <motion.circle cx="205" cy="105" r="15" stroke={T} strokeWidth="1.5" {...draw(0.8, 0.9)} />
    <motion.circle cx="205" cy="105" r="4.5" fill={T} {...fade(1.1)} />
    <motion.path {...draw(1.1, 0.7)} d="M221 105 H246" stroke={T} strokeWidth="1.5" strokeLinecap="round" />
    <motion.path {...fade(1.4)} d="M250 105 L246 101 M250 105 L246 109" stroke={T} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const GlyphSports = () => (
  <svg viewBox="0 0 64 64" fill="none" className="w-14 h-14" aria-hidden="true">
    <circle cx="54" cy="10" r="3.4" stroke={T} strokeWidth="2.3" />
    <ellipse cx="17" cy="7" rx="4.6" ry="6.2" transform="rotate(-32 17 7)" stroke={T} strokeWidth="2.2" />
    <path d="M14.5 5.5 L19.5 8.5 M15.8 3.6 L20.8 6.6" stroke={T} strokeOpacity="0.45" strokeWidth="1.2" strokeLinecap="round" />
    <circle cx="32" cy="12" r="4.4" stroke={T} strokeWidth="2.3" />
    <path
      d="M32 17 L33 34 M32 20 L23 11 M32 21 L43 15 M33 34 L28 46 L24 56 M33 34 L38 46 L37 56"
      stroke={T} strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"
    />
    <path d="M23 11 L20.5 9.6" stroke={T} strokeWidth="2.3" strokeLinecap="round" />
  </svg>
);

export const GlyphDefence = () => (
  <svg viewBox="0 0 64 64" fill="none" className="w-14 h-14" aria-hidden="true">
    <path
      d="M32 6 L54 14 V34 C54 46 44 54 32 58 C20 54 10 46 10 34 V14 Z"
      stroke={T} strokeWidth="2.2" strokeLinejoin="round"
    />
    <path
      d="M32 15 L34.2 19.6 L39.2 20 L35.4 23.2 L36.6 28 L32 25.4 L27.4 28 L28.6 23.2 L24.8 20 L29.8 19.6 Z"
      stroke={T} strokeWidth="1.8" strokeLinejoin="round"
    />
    <path
      d="M20 33 L32 38.5 L44 33 M20 40 L32 45.5 L44 40 M20 47 L32 52.5 L44 47"
      stroke={T} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
);

export const GlyphAviation = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-14 h-14" aria-hidden="true">
    <path
      d="M24 5 C26 9 26.5 13 26.5 17 L41 29 L41 33 L26.5 26 L26.5 36 L32 40.5 L32 43.5 L24 41.5 L16 43.5 L16 40.5 L21.5 36 L21.5 26 L7 33 L7 29 L21.5 17 C21.5 13 22 9 24 5 Z"
      stroke={T} strokeWidth="1.6" strokeLinejoin="round"
    />
    <path d="M24 12 V38" stroke={T} strokeOpacity="0.35" strokeWidth="1" strokeDasharray="2 4" />
  </svg>
);

export const PillarNeuro = () => (
  <svg viewBox="0 0 64 64" fill="none" className="w-11 h-11" aria-hidden="true">
    <motion.path {...draw(0, 1.4)} d="M32 8 C19 8 10 19 10 32 C10 45 19 56 32 56 C45 56 54 45 54 32 C54 19 45 8 32 8 Z" stroke={T} strokeWidth="1.6" />
    <motion.path {...draw(0.3, 1)} d="M32 8 V56" stroke={T} strokeOpacity="0.5" strokeWidth="1.3" strokeDasharray="3 4" />
    <motion.path {...draw(0.5, 0.9)} d="M18 24 C22 20 27 22 27 28 M17 38 C21 34 26 36 26 42" stroke={T} strokeWidth="1.3" strokeLinecap="round" />
    <motion.path {...draw(0.7, 0.9)} d="M46 24 C42 20 37 22 37 28 M47 38 C43 34 38 36 38 42" stroke={T} strokeWidth="1.3" strokeLinecap="round" />
    {[[24, 33], [40, 33]].map(([cx, cy], i) => (
      <motion.circle key={i} cx={cx} cy={cy} r="1.8" fill={T} {...fade(1 + i * 0.15)} />
    ))}
  </svg>
);

export const PillarAI = () => (
  <svg viewBox="0 0 64 64" fill="none" className="w-11 h-11" aria-hidden="true">
    {[0, 60, 120, 180, 240, 300].map((a, i) => {
      const rad = ((a - 90) * Math.PI) / 180;
      const x = 32 + Math.cos(rad) * 20;
      const y = 32 + Math.sin(rad) * 20;
      return (
        <g key={a}>
          <motion.line x1="32" y1="32" x2={x} y2={y} stroke={T} strokeOpacity="0.5" strokeWidth="1.2" {...draw(0.2 + i * 0.1, 0.7)} />
          <motion.circle cx={x} cy={y} r="3" stroke={T} strokeWidth="1.4" {...fade(0.5 + i * 0.1)} />
        </g>
      );
    })}
    <motion.circle cx="32" cy="32" r="4" fill={T} {...fade(0.9)} />
    <motion.circle cx="32" cy="32" r="26" stroke={T} strokeOpacity="0.15" strokeDasharray="2 6" {...fade(1)} />
  </svg>
);

export const PillarTech = () => (
  <svg viewBox="0 0 64 64" fill="none" className="w-11 h-11" aria-hidden="true">
    <motion.circle cx="32" cy="34" r="10" stroke={T} strokeWidth="1.6" {...draw(0, 1.2)} />
    <motion.path {...draw(0.4, 1.2)} d="M6 40 C14 30 50 26 58 22" stroke={T} strokeOpacity="0.6" strokeWidth="1.3" strokeLinecap="round" />
    <motion.path {...draw(0.6, 1.2)} d="M6 46 C16 40 48 38 58 34" stroke={T} strokeOpacity="0.25" strokeWidth="1.2" strokeDasharray="3 5" strokeLinecap="round" />
    <motion.circle cx="50" cy="25" r="2.4" fill={T} {...fade(1)} />
    <motion.path {...fade(1.1)} d="M50 17 V21 M50 29 V33 M42 25 H46 M54 25 H58" stroke={T} strokeOpacity="0.5" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);
